import React from "react";
import { useState } from "react";
import {
  DialogActions,
  TextField,
  IconButton,
  Box,
  Tooltip,
  Collapse,
  Typography,
  Chip,
  Paper,
} from "@mui/material";
import { Send as SendIcon, Help as HelpIcon } from "@mui/icons-material";
import { hasFormatting, FORMATTING_EXAMPLES } from "../utils";
import { FormattedText } from "./FormattedText";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onKeyPress: (event: React.KeyboardEvent) => void;
  disabled: boolean;
  maxRows?: number;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  onKeyPress,
  disabled,
  maxRows = 3,
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Show preview when text has formatting
  const shouldShowPreview = hasFormatting(value) && value.trim().length > 0;

  // Auto-show preview when typing formatted text
  React.useEffect(() => {
    setShowPreview(shouldShowPreview);
  }, [shouldShowPreview]);

  const insertFormatting = (format: string) => {
    const textarea = document.querySelector(
      'textarea[placeholder*="Escribe tu mensaje"]'
    ) as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    let newText = "";
    let cursorOffset = 0;

    switch (format) {
      case "bold":
        newText =
          value.substring(0, start) +
          `**${selectedText}**` +
          value.substring(end);
        cursorOffset = selectedText ? 2 : 2;
        break;
      case "italic":
        newText =
          value.substring(0, start) +
          `*${selectedText}*` +
          value.substring(end);
        cursorOffset = selectedText ? 1 : 1;
        break;
      case "strikethrough":
        newText =
          value.substring(0, start) +
          `~~${selectedText}~~` +
          value.substring(end);
        cursorOffset = selectedText ? 2 : 2;
        break;
      case "code":
        newText =
          value.substring(0, start) +
          `\`${selectedText}\`` +
          value.substring(end);
        cursorOffset = selectedText ? 1 : 1;
        break;
      default:
        return;
    }

    onChange(newText);

    // Set cursor position after formatting
    setTimeout(() => {
      const newCursorPos = selectedText
        ? end + cursorOffset
        : start + cursorOffset;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 10);
  };

  return (
    <>
      {/* Preview */}
      <Collapse in={showPreview}>
        <Box
          sx={{
            p: 2,
            pt: 1,
            bgcolor: "grey.50",
            borderTop: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            Vista previa:
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: "primary.main",
              color: "white",
              borderRadius: 2,
              borderTopRightRadius: 0,
              maxWidth: "70%",
              ml: "auto",
            }}
          >
            <FormattedText text={value} variant="body2" color="inherit" />
          </Paper>
        </Box>
      </Collapse>

      {/* Help */}
      <Collapse in={showHelp}>
        <Box
          sx={{
            p: 2,
            pt: 1,
            bgcolor: "info.50",
            borderTop: "1px solid",
            borderColor: "info.200",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            Formato Markdown disponible:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {FORMATTING_EXAMPLES.map((example, index) => (
              <Tooltip key={index} title={example.example} arrow>
                <Chip
                  label={`${example.format} = ${example.description}`}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const format = example.format.includes("**")
                      ? "bold"
                      : example.format.includes("*")
                      ? "italic"
                      : example.format.includes("~~")
                      ? "strikethrough"
                      : example.format.includes("`")
                      ? "code"
                      : "";
                    if (format) insertFormatting(format);
                  }}
                  sx={{ cursor: "pointer" }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      </Collapse>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
          {/* Only Help button */}
          <Tooltip
            title={showHelp ? "Ocultar ayuda" : "Mostrar ayuda de formato"}
          >
            <IconButton
              size="small"
              onClick={() => setShowHelp(!showHelp)}
              color={showHelp ? "primary" : "default"}
            >
              <HelpIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <TextField
            fullWidth
            multiline
            maxRows={maxRows}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyPress={onKeyPress}
            placeholder={
              "Escribe tu mensaje..." +
              FORMATTING_EXAMPLES.map((ele) => ele.format).join(", ")
            }
            variant="outlined"
            size="small"
            disabled={disabled}
            sx={{
              "& .MuiInputBase-input": {
                fontFamily: hasFormatting(value) ? "inherit" : "inherit",
              },
            }}
          />

          <IconButton
            color="primary"
            onClick={onSend}
            disabled={!value.trim() || disabled}
            sx={{ alignSelf: "flex-end" }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </DialogActions>
    </>
  );
};
